import React, { useState } from "react";
import { History, AlertTriangle, X, Info, Truck, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { EquipmentTypeahead } from "../../components/EquipmentTypeahead";
import { IssueNumberTypeahead } from "../../components/IssueNumberTypeahead";
import { ManufacturerTypeahead } from "../../components/ManufacturerTypeahead";
import { SubcontractorTypeahead } from "../../components/SubcontractorTypeahead";
import { SUBCON_RETURN_REASONS } from "../../components/ReturnToSubconModal";
import { fmtDate } from "../../lib/format";
import { toast } from "sonner";

const DISCIPLINES = ["Dimensional", "Pressure", "Torque", "Temperature", "Electronics", "Hydraulics", "Mass", "Flow"];
const SUBCON_REASONS = SUBCON_RETURN_REASONS;
const CONDITIONS = ["In box/case/pouch", "Arrived without box", "Visibly damaged", "Missing accessories", "Dirty"];

/**
 * Single equipment line-item card on the Receiving form. Collapsible:
 * - new/empty lines open by default so the user can start typing
 * - filled lines collapse to a one-line summary (description · SN/AN · discipline)
 * - missing required fields (description) are flagged in red when collapsed
 */
export function ReceivingLineItem({
  it, i, total,
  customer, techs, subcontractors,
  jobToSubcon, jobSubconName, jobSubconReason,
  setItem, removeItem, applyMatch, onOpenHistory,
}) {
  // Default: expanded if no description yet (user is still capturing this line).
  const [open, setOpen] = useState(!it.description);
  const hintName = it.last_subcontractor_name
    || (it.last_subcontractor_id && subcontractors.find((s) => s.id === it.last_subcontractor_id)?.name)
    || null;
  const explainCreateHint = () =>
    toast.message("OK — fill in the rest of the fields to register this as a new tool.");

  const missing = [];
  if (!it.description) missing.push("Description");
  if (it.send_to_subcontractor && !jobToSubcon) {
    if (!it.subcontractor_id) missing.push("Subcontractor");
    if (!it.subcontractor_reason) missing.push("Sub Con reason");
  }

  // One-line summary for the collapsed header
  const idBits = [
    it.serial_number ? `SN ${it.serial_number}` : null,
    it.part_number ? `PN ${it.part_number}` : null,
    it.issue_number ? `Issue ${it.issue_number}` : null,
  ].filter(Boolean);
  const summary = [
    it.description || "(no description)",
    idBits.length ? idBits.join(" · ") : null,
    it.discipline,
    it.technician_id && techs.find((t) => t.id === it.technician_id)?.name,
  ].filter(Boolean).join("  ·  ");

  return (
    <div className="line-item">
      <div className="lh" style={{ alignItems: "center" }}>
        <button type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`item-toggle-${i}`}
          style={{ background: "none", border: "none", padding: 0, marginRight: 6, display: "inline-flex", alignItems: "center", color: "var(--text-2)", cursor: "pointer" }}
          title={open ? "Collapse line" : "Expand line"}>
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        <div className="n" style={{ flex: 1, minWidth: 0 }}>
          <span className="li-chip">{i + 1}</span>
          {open ? (
            <span>{it.description || "New item"}</span>
          ) : (
            <span data-testid={`item-summary-${i}`}
              style={{ display: "inline-block", maxWidth: "calc(100% - 40px)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>
              {summary}
            </span>
          )}
          {it.matched_equipment_id && open && (
            <span className="badge b-green" style={{ marginLeft: 10 }} data-testid={`item-matched-${i}`}>
              <span className="bd" /> Existing record — last cal {fmtDate(it.matched_meta?.last)} · {it.matched_meta?.history || 0} event(s)
              <button className="icon-btn" style={{ marginLeft: 6 }} title="View history" onClick={() => onOpenHistory(it.matched_equipment_id)}>
                <History size={13} />
              </button>
            </span>
          )}
          {it.in_service_warning && open && (
            <span className="badge b-amber" style={{ marginLeft: 8 }}
              data-testid={`item-in-service-${i}`}
              title="This equipment is already on another active job">
              <AlertTriangle size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Already in service · DN <span className="mono">{it.in_service_warning.delivery_note_number || "—"}</span>
              {it.in_service_warning.status && <> · {it.in_service_warning.status.replace(/_/g, " ")}</>}
            </span>
          )}
          {it.cross_customer_warning && open && (
            <span className="badge" style={{ marginLeft: 8, background: "var(--red-bg, #fde7e7)", color: "var(--red, #b3261e)" }}
              data-testid={`item-cross-customer-warning-${i}`}
              title="This serial number is registered under a different customer">
              <AlertTriangle size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Duplicate risk — SN already registered under <b>{it.cross_customer_warning.customer_name || "another customer"}</b>
              {it.cross_customer_warning.description && <> ({it.cross_customer_warning.description})</>}
            </span>
          )}
          {it.unserviceable_flag && open && (
            <span className="badge" style={{ marginLeft: 8, background: "var(--red-bg, #fde7e7)", color: "var(--red, #b3261e)" }}
              data-testid={`item-unserviceable-warning-${i}`}
              title="This issue number belongs to an unserviceable/scrapped item">
              <AlertTriangle size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {it.unserviceable_flag.acked ? "Confirmed to use unserviceable item" : "Needs confirmation — unserviceable item"}
            </span>
          )}
        </div>
        {!open && missing.length > 0 && (
          <span style={{ display: "inline-flex", gap: 4, marginRight: 8, flexWrap: "wrap" }} data-testid={`item-missing-${i}`}>
            {missing.slice(0, 3).map((m) => (
              <span key={m} className="badge" style={{ background: "var(--red-bg, #fde7e7)", color: "var(--red, #b3261e)", fontSize: 10.5, gap: 4 }}>
                <AlertTriangle size={10} /> {m}
              </span>
            ))}
          </span>
        )}
        {!open && it.unserviceable_flag && !it.unserviceable_flag.acked && (
          <span className="badge" style={{ marginRight: 8, background: "var(--red-bg, #fde7e7)", color: "var(--red, #b3261e)", fontSize: 10.5, gap: 4 }}
            data-testid={`item-unserviceable-warning-collapsed-${i}`}
            title="Needs confirmation — unserviceable item">
            <AlertTriangle size={10} /> Confirm unserviceable
          </span>
        )}
        {!open && it.cross_customer_warning && (
          <span className="badge" style={{ marginRight: 8, background: "var(--red-bg, #fde7e7)", color: "var(--red, #b3261e)", fontSize: 10.5, gap: 4 }}
            data-testid={`item-cross-customer-warning-collapsed-${i}`}
            title={`SN already registered under ${it.cross_customer_warning.customer_name || "another customer"}`}>
            <AlertTriangle size={10} /> Duplicate SN
          </span>
        )}
        {!open && missing.length === 0 && !(it.unserviceable_flag && !it.unserviceable_flag.acked) && (
          <CheckCircle2 size={14} color="var(--green, #1f8a4d)" style={{ marginRight: 8 }} />
        )}
        {total > 1 && <button className="btn sm ghost" onClick={() => removeItem(i)}><X size={13} /></button>}
      </div>
      {open && (
      <>
      <div className="form-grid three">
        <div className="fld">
          <label>Serial number</label>
          <EquipmentTypeahead customerId={customer?.id} field="serial_number"
            value={it.serial_number} placeholder="Type to search existing…"
            onChange={(v) => setItem(i, { serial_number: v, matched_equipment_id: null })}
            onPick={(m) => applyMatch(i, m, "serial_number")}
            onCreateNew={explainCreateHint}
            onCrossCustomerMatch={(cm) => setItem(i, { cross_customer_warning: cm[0] || null })}
            testid={`item-serial-${i}`} />
        </div>
        <div className="fld">
          <label>Part number</label>
          <EquipmentTypeahead customerId={customer?.id} field="part_number" minimal
            value={it.part_number} placeholder="Type to search existing…"
            onChange={(v) => setItem(i, { part_number: v, matched_equipment_id: null })}
            onPick={(m) => applyMatch(i, m, "part_number")}
            onCreateNew={explainCreateHint}
            testid={`item-part-${i}`} />
        </div>
        <div className="fld">
          <label>Issue number</label>
          <IssueNumberTypeahead
            value={it.issue_number}
            onChange={(v) => setItem(i, { issue_number: v })}
            onPick={(m) => applyMatch(i, m, "issue_number")}
            onUnserviceableStatusChange={(v) => setItem(i, { unserviceable_flag: v })}
            placeholder="Unique ID when there is no serial number"
            testid={`item-issue-${i}`} />
        </div>
        <div className="fld"><label>Description <span className="req">*</span></label>
          <EquipmentTypeahead customerId={customer?.id} field="description" minimal
            value={it.description} placeholder="Type equipment name…"
            onChange={(v) => setItem(i, { description: v, matched_equipment_id: null })}
            onPick={(m) => applyMatch(i, m, "description")}
            testid={`item-desc-${i}`} />
        </div>
        <div className="fld"><label>Manufacturer</label>
          <ManufacturerTypeahead
            value={it.manufacturer}
            onChange={(v) => setItem(i, { manufacturer: v })}
            placeholder="Type to search existing…"
            testid={`item-manufacturer-${i}`} />
        </div>
        <div className="fld"><label>Discipline</label><select className="input" value={it.discipline} onChange={(e) => setItem(i, { discipline: e.target.value })}>{DISCIPLINES.map((d) => <option key={d}>{d}</option>)}</select></div>
        <div className="fld"><label>SANAS / Traceable</label>
          <select className="input" value={it.cal_method} onChange={(e) => setItem(i, { cal_method: e.target.value })}>
            <option value="traceable">Traceable</option><option value="sanas">SANAS Accredited</option>
          </select>
        </div>
        <div className="fld"><label>Owner of Tool</label><input className="input" value={it.location} onChange={(e) => setItem(i, { location: e.target.value })} placeholder="e.g. customer's own name on the tool" data-testid={`item-owner-${i}`} /></div>
        <div className="fld"><label>Technician</label><select className="input" value={it.technician_id} onChange={(e) => setItem(i, { technician_id: e.target.value })}><option value="">— assign —</option>{techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
        <div className="fld"><label>Cal. period</label>
          <select className="input mono" value={it.calibration_period_days || ""} onChange={(e) => setItem(i, { calibration_period_days: e.target.value })} data-testid={`item-calperiod-${i}`}>
            <option value="">— select —</option>
            <option value="180">180 days (6 months)</option>
            <option value="365">365 days (1 year)</option>
            <option value="730">730 days (2 years)</option>
            <option value="1095">1095 days (3 years)</option>
            <option value="1460">1460 days (4 years)</option>
            <option value="1825">1825 days (5 years)</option>
          </select>
        </div>
        <div className="fld"><label>Priority</label>
          <select className="input" value={it.priority} onChange={(e) => setItem(i, { priority: e.target.value })} data-testid={`item-priority-${i}`}>
            <option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
          </select>
        </div>
        <div className="fld"><label>Receiving condition</label>
          <select className="input" value={it.receiving_condition} onChange={(e) => setItem(i, { receiving_condition: e.target.value })}>
            {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="fld"><label>Remarks</label><input className="input" value={it.remarks} onChange={(e) => setItem(i, { remarks: e.target.value })} placeholder="No remarks" /></div>
        <div className="fld" style={{ gridColumn: "span 2" }}><label>Notes</label><textarea className="input" rows={2} value={it.notes} onChange={(e) => setItem(i, { notes: e.target.value })} /></div>
        <div className="fld" style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={it.group_item}
              onChange={(e) => setItem(i, { group_item: e.target.checked, group_number: e.target.checked ? it.group_number : "" })}
              data-testid={`item-group-toggle-${i}`} /> Part of a group
          </label>
          {it.group_item && (
            <input className="input mono" style={{ width: 130 }} value={it.group_number || ""}
              onChange={(e) => setItem(i, { group_number: e.target.value })}
              placeholder="Group number e.g. BK309"
              data-testid={`item-group-number-${i}`} />
          )}
        </div>
      </div>

      {/* Subcontractor routing — line-level (suppressed when the whole job is routed) */}
      {jobToSubcon ? (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--line)", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}
          className="t-mut" data-testid={`item-subcon-jobwide-note-${i}`}>
          <Truck size={14} color="var(--gold-deep)" /> Goes to <b>{jobSubconName || "the selected subcontractor"}</b> with the whole job{jobSubconReason ? ` · ${jobSubconReason}` : ""}.
        </div>
      ) : (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--line)" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={!!it.send_to_subcontractor}
              onChange={(e) => setItem(i, { send_to_subcontractor: e.target.checked })}
              data-testid={`item-subcon-toggle-${i}`} />
            <Truck size={14} color="var(--gold-deep)" /> Send to subcontractor
          </label>
          {hintName && !it.send_to_subcontractor && (
            <div className="t-mut" style={{ fontSize: 12, marginTop: 6, display: "flex", alignItems: "center", gap: 8 }} data-testid={`item-subcon-hint-${i}`}>
              <Info size={12} /> Previously subcontracted to <b>{hintName}</b>.
              <button style={{ fontSize: 12, color: "var(--gold-deep)", background: "none", border: "none", textDecoration: "underline", cursor: "pointer", padding: 0 }} onClick={() => setItem(i, {
                send_to_subcontractor: true,
                subcontractor_id: it.last_subcontractor_id || subcontractors.find((s) => s.name === hintName)?.id || "",
                subcontractor_name: hintName,
              })} data-testid={`item-subcon-usehint-${i}`}>Use again</button>
            </div>
          )}
          {it.send_to_subcontractor && (
            <div className="form-grid" style={{ marginTop: 10 }}>
              <div className="fld"><label>Subcontractor <span className="req">*</span></label>
                <SubcontractorTypeahead
                  value={it.subcontractor_name || ""}
                  onChange={(v) => setItem(i, { subcontractor_name: v, subcontractor_id: "" })}
                  onPick={(sc) => setItem(i, { subcontractor_id: sc.id, subcontractor_name: sc.name })}
                  activeOnly allowFreeText={false}
                  placeholder="Type to search existing subcontractors…"
                  testid={`item-subcon-select-${i}`} />
              </div>
              <div className="fld"><label>Sub Con reason <span className="req">*</span></label>
                <select className="input" value={it.subcontractor_reason || ""}
                  onChange={(e) => setItem(i, { subcontractor_reason: e.target.value })}
                  data-testid={`item-subcon-reason-${i}`}>
                  <option value="">— select reason —</option>
                  {SUBCON_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="fld"><label>Delivery method</label>
                <select className="input" value={it.delivery_method || ""}
                  onChange={(e) => setItem(i, { delivery_method: e.target.value })}
                  data-testid={`item-subcon-delivery-${i}`}>
                  <option value="">— if known —</option>
                  <option value="pte_deliver">PTE deliver</option>
                  <option value="courier">Courier</option>
                  <option value="subcontractor_collect">Subcontractor collect</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}
